import React, { useEffect, useMemo, useState } from "react";
import { api, fmtUSD, fmtNumber, fmtDate } from "../lib/api";
import KpiCard from "../components/KpiCard";
import HierarchyExplorer from "../components/HierarchyExplorer";
import {
  Button,
} from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, CartesianGrid,
  XAxis, YAxis, Tooltip, BarChart, Bar, Cell, ReferenceLine, Legend,
} from "recharts";
import {
  DollarSign, TrendingUp, Users, AlertTriangle, Activity,
  Layers, Calculator, ArrowRight, Sparkles, ChevronRight,
  CheckCircle2, AlertOctagon, Rocket,
} from "lucide-react";

const STATUS = {
  profitable: { color: "#15803d", bg: "#dcfce7", label: "Profitable", icon: CheckCircle2 },
  at_risk:    { color: "#92400e", bg: "#fef3c7", label: "At Risk",    icon: AlertTriangle },
  unprofitable: { color: "#b91c1c", bg: "#fee2e2", label: "Unprofitable", icon: AlertOctagon },
};

const TIER_STYLES = {
  Enterprise: "bg-[#dbeafe] text-[#00579e]",
  Growth: "bg-[#ede9fe] text-[#6d28d9]",
  Starter: "bg-[#f3f4f6] text-[#374151]",
};

const SERVICE_COLORS = {
  BigQuery: "#00579e", "Cloud SQL": "#15803d", "Compute Engine": "#9a3412",
  "Vertex AI": "#6d28d9", GKE: "#0e7490", "Cloud Storage": "#374151", "Cloud Run": "#be185d",
};

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.at_risk;
  const Icon = s.icon;
  return (
    <span
      data-testid={`status-${status}`}
      className="cs-pill"
      style={{ color: s.color, background: s.bg, borderColor: s.color + "33" }}
    >
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

export default function UnitEconomics() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [trends, setTrends] = useState([]);
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);

  useEffect(() => {
    Promise.all([
      api.ue.summary(days),
      api.ue.customers(days),
      api.ue.products(days),
      api.ue.trends(days),
      api.ue.events(),
      api.ue.metrics(),
    ]).then(([s, c, p, t, e, m]) => {
      setSummary(s); setCustomers(c); setProducts(p); setTrends(t); setEvents(e); setMeta(m);
    }).catch(console.error);
  }, [days]);

  const openCustomer = async (c) => {
    setSelectedCustomer(c);
    try {
      const d = await api.ue.customer(c.id, days);
      setCustomerDetail(d);
    } catch (e) { console.error(e); }
  };

  if (!summary) {
    return <div className="px-6 lg:px-10 py-10 text-muted-foreground">Loading unit economics…</div>;
  }

  return (
    <div className="px-6 lg:px-10 py-8 space-y-6" data-testid="unit-economics-page">
      {/* Header controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            Cost-per-customer, cost-per-product, and cost-per-unit attribution across all GCP services.
          </p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
          <SelectTrigger className="w-[140px] rounded-full h-9" data-testid="ue-period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[7, 14, 30, 60, 90].map(d => (
              <SelectItem key={d} value={String(d)}>Last {d} days</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard testId="ue-kpi-revenue" label="Revenue" value={fmtUSD(summary.total_revenue_usd)} sublabel={`Last ${days} days`} icon={DollarSign} tone="success" />
        <KpiCard testId="ue-kpi-cost" label="Cost to serve" value={fmtUSD(summary.total_cost_usd)} sublabel="Attributed cloud spend" icon={Activity} tone="warning" />
        <KpiCard testId="ue-kpi-margin" label="Gross margin" value={`${summary.margin_pct}%`} sublabel={fmtUSD(summary.total_margin_usd)} icon={TrendingUp} tone="primary" />
        <KpiCard testId="ue-kpi-customers" label="Customers" value={fmtNumber(summary.customer_count)} sublabel={`${summary.profitable_count} profitable · ${summary.at_risk_count} at risk`} icon={Users} tone="teal" />
        <KpiCard testId="ue-kpi-avg-cost" label="Avg per customer" value={fmtUSD(summary.avg_cost_per_customer_usd)} sublabel={`Revenue avg ${fmtUSD(summary.avg_revenue_per_customer_usd)}`} icon={Layers} tone={summary.unprofitable_count ? "danger" : "neutral"} />
      </div>

      <Tabs defaultValue="hierarchy" className="space-y-4">
        <TabsList className="bg-white border border-border rounded-full p-1 h-auto">
          {[
            { v: "hierarchy", l: "BU → Service Hierarchy" },
            { v: "customers", l: "Cost Per Customer" },
            { v: "products",  l: "Cost Per Product" },
            { v: "calculator", l: "Unit Cost Calculator" },
            { v: "trends",    l: "Trends & Events" },
            { v: "forecast",  l: "What-If" },
          ].map(t => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`ue-tab-${t.v}`}
              className="rounded-full data-[state=active]:bg-[#00579e] data-[state=active]:text-white text-xs px-4 py-1.5">
              {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* HIERARCHY TAB */}
        <TabsContent value="hierarchy">
          <HierarchyExplorer days={days} />
        </TabsContent>

        {/* CUSTOMERS TAB */}
        <TabsContent value="customers">
          <div className="cs-card overflow-hidden" data-testid="ue-customers-card">
            <div className="px-6 py-4 border-b border-border">
              <h2 className="cs-heading text-base font-semibold tracking-tight">Customer Profitability</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Each customer's cloud spend, revenue, margin & profitability status. Click a row for detail.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Customer</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Tier</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Primary Unit</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Units</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Unit Cost</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Cost</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Revenue</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Margin</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map(c => (
                    <tr key={c.id} onClick={() => openCustomer(c)} data-testid={`ue-customer-row-${c.id}`} className="cs-row-hover border-b border-border last:border-0 cursor-pointer">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{c.name}</div>
                        <div className="text-[11px] text-muted-foreground">{c.industry}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`cs-pill ${TIER_STYLES[c.tier]}`}>{c.tier}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground capitalize">{c.primary_metric?.replace(/_/g, " ")}</td>
                      <td className="px-4 py-3 text-right cs-mono text-xs">{fmtNumber(Math.round(c.primary_units))}</td>
                      <td className="px-4 py-3 text-right cs-mono text-xs">${c.unit_cost_usd?.toFixed(4) || "—"}</td>
                      <td className="px-4 py-3 text-right cs-mono">{fmtUSD(c.total_cost_usd)}</td>
                      <td className="px-4 py-3 text-right cs-mono font-medium">{fmtUSD(c.total_revenue_usd)}</td>
                      <td className="px-4 py-3 text-right cs-mono font-semibold" style={{ color: c.total_margin_usd >= 0 ? "#15803d" : "#b91c1c" }}>
                        {fmtUSD(c.total_margin_usd)}
                        <div className="text-[10px] font-normal text-muted-foreground">{c.margin_pct}%</div>
                      </td>
                      <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                      <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-muted-foreground inline" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* PRODUCTS TAB */}
        <TabsContent value="products">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {products.map(p => (
              <div key={p.id} className="cs-card p-5" data-testid={`ue-product-${p.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{p.primary_services.join(" · ")}</div>
                    <h3 className="cs-heading text-base font-semibold tracking-tight mt-0.5">{p.name}</h3>
                  </div>
                  <span className="text-xs cs-mono text-muted-foreground">{p.customer_count} customers</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Cost</div>
                    <div className="cs-kpi-value text-lg text-foreground mt-1">{fmtUSD(p.total_cost_usd)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Revenue</div>
                    <div className="cs-kpi-value text-lg text-foreground mt-1">{fmtUSD(p.total_revenue_usd)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Margin</div>
                    <div className="cs-kpi-value text-lg mt-1" style={{ color: p.total_margin_usd >= 0 ? "#15803d" : "#b91c1c" }}>{p.margin_pct}%</div>
                  </div>
                </div>
                {/* margin bar */}
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Cost / Revenue split</div>
                  <div className="h-2 w-full bg-[hsl(var(--cs-tint))] rounded-full overflow-hidden flex">
                    <div className="h-full" style={{ width: `${100 - p.margin_pct}%`, background: "#f59e0b" }} />
                    <div className="h-full" style={{ width: `${p.margin_pct}%`, background: "#15803d" }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* CALCULATOR TAB */}
        <TabsContent value="calculator">
          <Calculator_ meta={meta} days={days} />
        </TabsContent>

        {/* TRENDS TAB */}
        <TabsContent value="trends">
          <div className="cs-card p-6" data-testid="ue-trend-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="cs-heading text-base font-semibold tracking-tight">Cost · Revenue · Margin trend</h2>
                <p className="text-xs text-muted-foreground mt-0.5">With engineering events overlaid</p>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trends} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#6b7280" interval="preserveStartEnd" minTickGap={40}
                         tickFormatter={(d) => fmtDate(d + "T00:00:00Z").split(",")[0]} />
                  <YAxis tick={{ fontSize: 10 }} stroke="#6b7280" tickFormatter={(v) => `$${Math.round(v/1000)}k`} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={(v) => fmtUSD(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="revenue_usd" name="Revenue" stroke="#00579e" strokeWidth={2.5} fill="#00579e" fillOpacity={0.22} />
                  <Area type="monotone" dataKey="cost_usd"    name="Cost"    stroke="#f59e0b" strokeWidth={2}   fill="#f59e0b" fillOpacity={0.18} />
                  {events.map((e, i) => (
                    <ReferenceLine key={i} x={e.date} stroke="#6d28d9" strokeDasharray="2 3"
                                   label={{ value: "•", position: "top", fill: "#6d28d9" }} />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {events.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Engineering events</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {events.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-foreground">
                      <span className="cs-mono text-[10px] text-muted-foreground w-20 shrink-0">{e.date}</span>
                      <span className={`cs-pill ${e.type === "incident" ? "bg-[#fee2e2] text-[#b91c1c]" : "bg-[#ede9fe] text-[#6d28d9]"}`}>{e.type}</span>
                      <span className="text-muted-foreground truncate">{e.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* FORECAST TAB */}
        <TabsContent value="forecast">
          <ForecastView meta={meta} days={days} />
        </TabsContent>
      </Tabs>

      {/* Customer detail dialog */}
      <Dialog open={!!selectedCustomer} onOpenChange={(v) => !v && (setSelectedCustomer(null), setCustomerDetail(null))}>
        <DialogContent className="max-w-3xl rounded-xl" data-testid="ue-customer-detail-modal">
          {selectedCustomer && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <span className={`cs-pill ${TIER_STYLES[selectedCustomer.tier]}`}>{selectedCustomer.tier}</span>
                  <StatusPill status={selectedCustomer.status} />
                </div>
                <DialogTitle className="cs-heading text-xl tracking-tight mt-2">{selectedCustomer.name}</DialogTitle>
                <DialogDescription>
                  {selectedCustomer.industry} · Customer since {selectedCustomer.since}
                </DialogDescription>
              </DialogHeader>
              {customerDetail ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="border border-border p-3 rounded-lg">
                      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Cost</div>
                      <div className="cs-kpi-value text-xl text-foreground mt-1">{fmtUSD(customerDetail.total_cost_usd)}</div>
                    </div>
                    <div className="border border-border p-3 rounded-lg">
                      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Revenue</div>
                      <div className="cs-kpi-value text-xl text-foreground mt-1">{fmtUSD(customerDetail.total_revenue_usd)}</div>
                    </div>
                    <div className="border border-border p-3 rounded-lg">
                      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Margin</div>
                      <div className="cs-kpi-value text-xl mt-1" style={{ color: customerDetail.total_margin_usd >= 0 ? "#15803d" : "#b91c1c" }}>
                        {customerDetail.margin_pct}%
                      </div>
                      <div className="text-[10px] text-muted-foreground">{fmtUSD(customerDetail.total_margin_usd)}</div>
                    </div>
                  </div>

                  {/* Pricing model table */}
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Consumption pricing model</div>
                    <div className="border border-border rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-[hsl(var(--cs-tint))]">
                          <tr className="text-left">
                            <th className="px-3 py-2 font-medium text-muted-foreground">Action</th>
                            <th className="px-3 py-2 text-right font-medium text-muted-foreground">Units</th>
                            <th className="px-3 py-2 text-right font-medium text-muted-foreground">Cost / unit</th>
                            <th className="px-3 py-2 text-right font-medium text-muted-foreground">Charge / unit</th>
                            <th className="px-3 py-2 text-right font-medium text-muted-foreground">Margin</th>
                            <th className="px-3 py-2 text-right font-medium text-muted-foreground">Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customerDetail.pricing_model.map(p => (
                            <tr key={p.metric_id} className="border-t border-border" data-testid={`pricing-row-${p.metric_id}`}>
                              <td className="px-3 py-2">
                                <div className="font-medium text-foreground">{p.label}</div>
                                <div className="text-[10px] text-muted-foreground">{p.service}</div>
                              </td>
                              <td className="px-3 py-2 text-right cs-mono">{fmtNumber(Math.round(p.units_consumed))}</td>
                              <td className="px-3 py-2 text-right cs-mono">${p.cost_to_serve_usd.toFixed(4)}</td>
                              <td className="px-3 py-2 text-right cs-mono">${p.charge_price_usd.toFixed(4)}</td>
                              <td className="px-3 py-2 text-right cs-mono font-medium" style={{ color: "#15803d" }}>{p.margin_pct}%</td>
                              <td className="px-3 py-2 text-right cs-mono font-semibold">{fmtUSD(p.total_revenue_usd)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Daily trend */}
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Daily cost vs revenue</div>
                    <div className="h-44">
                      <ResponsiveContainer>
                        <LineChart data={customerDetail.trend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#6b7280" tickFormatter={(d) => fmtDate(d + "T00:00:00Z").split(",")[0]} />
                          <YAxis tick={{ fontSize: 10 }} stroke="#6b7280" tickFormatter={(v) => `$${Math.round(v)}`} />
                          <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtUSD(v)} />
                          <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#00579e" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="cost"    name="Cost"    stroke="#f59e0b" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Service mix */}
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Cost by GCP service</div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(customerDetail.cost_by_service).map(([s, v]) => (
                        <div key={s} className="border border-border rounded-lg px-3 py-1.5 text-xs">
                          <span className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{ background: SERVICE_COLORS[s] }} />
                          <span className="text-foreground">{s}</span>
                          <span className="ml-2 cs-mono font-medium">{fmtUSD(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : <div className="py-6 text-center text-muted-foreground">Loading…</div>}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Calculator subcomponent ─────────────────────────────────────────────────

function Calculator_({ meta, days }) {
  const [metricId, setMetricId] = useState("api_calls");
  const [customerId, setCustomerId] = useState("");
  const [productId, setProductId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calc = async () => {
    setLoading(true);
    try {
      const r = await api.ue.calculate({
        metric_id: metricId,
        customer_id: customerId || null,
        product_id: productId || null,
        days,
      });
      setResult(r);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { calc(); /* eslint-disable-next-line */ }, [metricId, customerId, productId, days]);

  if (!meta) return <div className="cs-card p-10 text-center text-muted-foreground">Loading metric catalog…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
      <div className="cs-card p-5 space-y-4" data-testid="ue-calculator-form">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-base font-semibold tracking-tight">Unit Cost Calculator</h3>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">Metric</label>
          <Select value={metricId} onValueChange={setMetricId}>
            <SelectTrigger className="rounded-lg" data-testid="calc-metric"><SelectValue /></SelectTrigger>
            <SelectContent>
              {meta.metrics.map(m => (
                <SelectItem key={m.id} value={m.id}>{m.label} ({m.service})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">Customer (optional)</label>
          <Select value={customerId || "all"} onValueChange={(v) => setCustomerId(v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-lg" data-testid="calc-customer"><SelectValue placeholder="All customers" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All customers</SelectItem>
              {meta.customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">Product (optional)</label>
          <Select value={productId || "all"} onValueChange={(v) => setProductId(v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-lg" data-testid="calc-product"><SelectValue placeholder="All products" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All products</SelectItem>
              {meta.products.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button onClick={calc} disabled={loading} data-testid="calc-run" className="w-full rounded-full bg-[#00579e] hover:bg-[#004580]">
          {loading ? "Calculating…" : <><Sparkles className="w-4 h-4 mr-1.5" /> Calculate</>}
        </Button>
      </div>

      <div className="space-y-4">
        {!result ? (
          <div className="cs-card p-10 text-center text-muted-foreground">Pick filters to see the unit cost…</div>
        ) : result.error ? (
          <div className="cs-card p-10 text-center text-[#b91c1c]">{result.error}</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="calc-results">
              <div className="cs-card p-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Unit cost</div>
                <div className="cs-kpi-value text-2xl text-[#00579e] mt-1.5">${result.unit_cost_usd.toFixed(4)}</div>
                <div className="text-[10px] text-muted-foreground mt-1">per {result.metric.label.toLowerCase()}</div>
              </div>
              <div className="cs-card p-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Unit revenue</div>
                <div className="cs-kpi-value text-2xl text-foreground mt-1.5">${result.unit_revenue_usd.toFixed(4)}</div>
                <div className="text-[10px] text-muted-foreground mt-1">our charge price</div>
              </div>
              <div className="cs-card p-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Margin</div>
                <div className="cs-kpi-value text-2xl mt-1.5" style={{ color: result.margin_pct >= 0 ? "#15803d" : "#b91c1c" }}>{result.margin_pct}%</div>
                <div className="text-[10px] text-muted-foreground mt-1">CER {result.cer}</div>
              </div>
              <div className="cs-card p-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Units consumed</div>
                <div className="cs-kpi-value text-2xl text-foreground mt-1.5">{fmtNumber(Math.round(result.total_units))}</div>
                <div className="text-[10px] text-muted-foreground mt-1">last {result.filters.days} days</div>
              </div>
            </div>

            <div className="cs-card p-5">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-3">P&L for this slice</div>
              <div className="space-y-3">
                <Row label="Total revenue" value={fmtUSD(result.total_revenue_usd)} bold />
                <Row label="Cost to serve" value={fmtUSD(result.total_cost_usd)} muted />
                <div className="border-t border-border pt-3">
                  <Row label="Gross margin" value={fmtUSD(result.total_margin_usd)} color={result.total_margin_usd >= 0 ? "#15803d" : "#b91c1c"} bold large />
                </div>
              </div>
            </div>

            <div className="cs-card p-5 bg-[hsl(var(--cs-tint))]" data-testid="calc-pricing-insight">
              <div className="flex items-start gap-3">
                <Rocket className="w-5 h-5 text-[#00579e] mt-0.5" />
                <div className="text-sm text-foreground">
                  <span className="font-semibold">Consumption-pricing insight:</span> At <span className="cs-mono">${result.unit_revenue_usd.toFixed(4)}</span>/unit charged vs <span className="cs-mono">${result.unit_cost_usd.toFixed(4)}</span>/unit cost, you earn{" "}
                  <span className="font-semibold text-[#15803d]">${(result.unit_revenue_usd - result.unit_cost_usd).toFixed(4)} per unit</span> — a {result.margin_pct}% gross margin. A customer doing 1M of these per month would generate{" "}
                  <span className="cs-mono font-semibold">{fmtUSD((result.unit_revenue_usd - result.unit_cost_usd) * 1_000_000)}</span> in monthly gross profit.
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, bold, color, large, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${muted ? "text-muted-foreground" : "text-foreground"}`}>{label}</span>
      <span className={`cs-mono ${bold ? "font-semibold" : ""} ${large ? "text-xl" : ""}`} style={color ? { color } : {}}>{value}</span>
    </div>
  );
}

// ── What-If subcomponent ────────────────────────────────────────────────────

function ForecastView({ meta, days }) {
  const [metricId, setMetricId] = useState("ml_predictions");
  const [multiplier, setMultiplier] = useState(2.0);
  const [base, setBase] = useState(null);

  useEffect(() => {
    api.ue.calculate({ metric_id: metricId, days }).then(setBase).catch(console.error);
  }, [metricId, days]);

  if (!meta) return <div className="cs-card p-10 text-center text-muted-foreground">Loading…</div>;

  const projected = base ? {
    units: base.total_units * multiplier,
    cost: base.total_cost_usd * multiplier,
    revenue: base.total_revenue_usd * multiplier,
    margin: (base.total_revenue_usd - base.total_cost_usd) * multiplier,
  } : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4" data-testid="ue-forecast-view">
      <div className="cs-card p-5 space-y-4">
        <h3 className="cs-heading text-base font-semibold tracking-tight">What-If Projection</h3>
        <p className="text-xs text-muted-foreground">If usage grows by Nx, what does cost / revenue / margin look like at the same unit economics?</p>

        <div>
          <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">Metric</label>
          <Select value={metricId} onValueChange={setMetricId}>
            <SelectTrigger className="rounded-lg" data-testid="forecast-metric"><SelectValue /></SelectTrigger>
            <SelectContent>
              {meta.metrics.map(m => (
                <SelectItem key={m.id} value={m.id}>{m.label} ({m.service})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">Growth factor: <span className="cs-mono font-semibold text-foreground">{multiplier.toFixed(1)}×</span></label>
          <input type="range" min={0.5} max={5} step={0.1} value={multiplier} onChange={(e) => setMultiplier(parseFloat(e.target.value))}
                 data-testid="forecast-slider"
                 className="w-full accent-[#00579e]" />
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1 cs-mono">
            <span>0.5×</span><span>1×</span><span>2×</span><span>3×</span><span>5×</span>
          </div>
        </div>

        <div className="flex gap-1 flex-wrap">
          {[0.5, 1, 2, 3, 5].map(m => (
            <Button key={m} variant="outline" size="sm" onClick={() => setMultiplier(m)} className="rounded-full h-7 text-xs">
              {m}×
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {!base || !projected ? (
          <div className="cs-card p-10 text-center text-muted-foreground">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="cs-card p-5">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Current ({days}d)</div>
                <div className="space-y-2 mt-3">
                  <Row label="Units" value={fmtNumber(Math.round(base.total_units))} />
                  <Row label="Cost" value={fmtUSD(base.total_cost_usd)} />
                  <Row label="Revenue" value={fmtUSD(base.total_revenue_usd)} />
                  <div className="border-t border-border pt-2">
                    <Row label="Margin" value={fmtUSD(base.total_margin_usd)} bold color="#15803d" />
                  </div>
                </div>
              </div>
              <div className="cs-card p-5 bg-[#dbeafe]/40 border-[#00579e]/30">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#00579e] font-semibold">At {multiplier.toFixed(1)}×</div>
                <div className="space-y-2 mt-3">
                  <Row label="Units" value={fmtNumber(Math.round(projected.units))} />
                  <Row label="Cost" value={fmtUSD(projected.cost)} />
                  <Row label="Revenue" value={fmtUSD(projected.revenue)} bold />
                  <div className="border-t border-[#00579e]/30 pt-2">
                    <Row label="Margin" value={fmtUSD(projected.margin)} bold large color="#15803d" />
                  </div>
                </div>
              </div>
            </div>

            <div className="cs-card p-5">
              <h4 className="cs-heading text-sm font-semibold mb-3">Projection chart</h4>
              <div className="h-48">
                <ResponsiveContainer>
                  <BarChart data={[
                    { stage: "Current", Cost: base.total_cost_usd, Revenue: base.total_revenue_usd, Margin: base.total_margin_usd },
                    { stage: `At ${multiplier.toFixed(1)}×`, Cost: projected.cost, Revenue: projected.revenue, Margin: projected.margin },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="stage" tick={{ fontSize: 11 }} stroke="#6b7280" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#6b7280" tickFormatter={(v) => `$${Math.round(v/1000)}k`} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtUSD(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Cost" fill="#f59e0b" radius={[4,4,0,0]} />
                    <Bar dataKey="Revenue" fill="#00579e" radius={[4,4,0,0]} />
                    <Bar dataKey="Margin" fill="#15803d" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
